import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { ActivityLog } from './useData';
import { formatDate as formatDateGlobal, formatDateTime, APP_CONFIG } from '../../config/appConfig';
import { Badge, StandardTableActions } from '../../components/ui';
import { FieldConfig } from '../../components/generic/fieldRenderers';

/** Parse user agent string to a short "Browser · OS" label (e.g. "Chrome 120 · Windows"). */
function parseUserAgentShort(ua: string | null | undefined): string {
  if (!ua || typeof ua !== 'string') return '—';
  const s = ua.trim();
  if (!s) return '—';
  const browserMatch = s.match(/(?:Chrome|Firefox|Safari|Edg|Opera|MSIE)[/\s](\d+)/i) || s.match(/(\w+)\/([\d.]+)/);
  const osMatch = s.match(/(?:Windows NT|Windows|Mac OS X|Linux|Android|iPhone OS|iPad)/i);
  const browser = browserMatch ? (browserMatch[0]?.replace(/\//g, ' ') ?? '').trim() : '';
  const os = osMatch
    ? osMatch[0]
        .replace('Windows NT 10.0', 'Windows')
        .replace('Windows NT', 'Windows')
        .replace('Mac OS X', 'Mac')
        .trim()
    : '';
  const parts = [browser, os].filter(Boolean);
  return parts.length ? parts.join(' · ') : s.length > 40 ? s.slice(0, 37) + '…' : s;
}

/** Format a date as relative time (e.g. "2 hours ago", "just now"). */
function formatRelativeTime(isoDate: string | null | undefined): string {
  if (!isoDate) return '—';
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return '—';
  const now = new Date();
  const sec = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (sec < 10) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 4) return `${w}w ago`;
  return formatDateTime(isoDate);
}

/** Build a short field-level diff summary for UPDATE (e.g. "clientName: 'A' → 'B'"). */
function getFieldLevelDiffSummary(before: Record<string, unknown>, after: Record<string, unknown>): Array<{ key: string; beforeVal: unknown; afterVal: unknown }> {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const lines: Array<{ key: string; beforeVal: unknown; afterVal: unknown }> = [];
  keys.forEach((key) => {
    const b = before[key];
    const a = after[key];
    const same = JSON.stringify(b) === JSON.stringify(a);
    if (!same) lines.push({ key, beforeVal: b, afterVal: a });
  });
  return lines;
}

function formatDiffValue(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return '—';
  if (typeof v === 'string') return `'${v}'`;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const ENTITY_TYPE_LABELS: Record<string, string> = {
  Client: 'Client',
  Task: 'Task',
  Ticket: 'Ticket',
  Lead: 'Lead',
  Note: 'Note',
  Todo: 'Todo',
  Project: 'Project',
  Package: 'Package',
  SupportAgreement: 'Support Agreement',
  Invoice: 'Invoice',
  InvoicePayment: 'Invoice Payment',
  Appointment: 'Appointment',
  Call: 'Call',
  Reminder: 'Reminder',
  ClientService: 'Client Service',
  PackageTask: 'Package Task',
  SupportAgreementTask: 'Support Agreement Task',
  ClientContact: 'Client Contact',
  TicketComment: 'Ticket Comment',
  Expense: 'Expense',
  Conversation: 'Conversation',
  FileAttachment: 'File Attachment',
  User: 'User',
  UserInvitation: 'User Invitation',
  EmailAccount: 'Email Account',
  KnowledgeBaseCategory: 'Knowledge Base Category',
  KnowledgeBaseArticle: 'Knowledge Base Article',
  CustomField: 'Custom Field',
  Workflow: 'Workflow',
  FilterPreset: 'Filter Preset',
  TablePreset: 'Table Preset',
  ConditionalFormattingRule: 'Conditional Formatting Rule',
  ReportTemplate: 'Report Template',
  DashboardLayout: 'Dashboard Layout',
  Tag: 'Tag',
  TodoType: 'Todo Type',
  TodoRecurrenceInterval: 'Todo Recurrence Interval',
  ProjectStatus: 'Project Status',
  TaxAuthority: 'Tax Authority',
  FrequencyType: 'Frequency Type',
  TicketStatus: 'Ticket Status',
  TaskType: 'Task Type',
  TaskStatus: 'Task Status',
  TaskCategory: 'Task Category',
  ServiceType: 'Service Type',
  ServiceCategory: 'Service Category',
  ReminderType: 'Reminder Type',
  Priority: 'Priority',
  InvoiceType: 'Invoice Type',
  InvoiceCategory: 'Invoice Category',
  Duration: 'Duration',
  AppStatus: 'App Status',
  Country: 'Country',
  CallType: 'Call Type',
  ExpenseCategory: 'Expense Category',
  ExpenseSubCategory: 'Expense Sub Category',
};

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Activity Logs',
    description: 'View system activity and audit trail',
    permissionModuleKey: 'activity_logs',
    route: {
      list: '/activity-logs',
      new: '/activity-logs/new',
      detail: (id: number) => `/activity-logs/${id}`,
      edit: (id: number) => `/activity-logs/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/activity-logs',
      detail: (id: number) => `/activity-logs/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search activity logs by user, entity, or action...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['userId', 'entityType', 'action', 'ipAddress'],
    },
    {
      name: 'dates',
      label: 'Dates',
      icon: '📅',
      fields: ['startDate', 'endDate'],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'userId',
        label: 'User',
        type: 'select' as const,
        placeholder: 'Select user',
        options: [],
        apiEndpoint: '/users',
        labelField: 'userDisplayName',
      },
      {
        key: 'entityType',
        label: 'Entity Type',
        type: 'select' as const,
        placeholder: 'Select entity type',
        options: [
          // Reuse ENTITY_TYPE_LABELS for consistent labels
          ...Object.entries(ENTITY_TYPE_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
        ],
      },
      {
        key: 'action',
        label: 'Action',
        type: 'select' as const,
        placeholder: 'Select action',
        options: [
          { value: 'CREATE', label: 'Create' },
          { value: 'UPDATE', label: 'Update' },
          { value: 'DELETE', label: 'Delete' },
        ],
      },
      {
        key: 'ipAddress',
        label: 'IP Address',
        type: 'text' as const,
        placeholder: 'e.g. 192.168 or full IP',
      },
      {
        key: 'startDate',
        label: 'Start Date',
        type: 'date' as const,
        placeholder: 'Select start date',
      },
      {
        key: 'endDate',
        label: 'End Date',
        type: 'date' as const,
        placeholder: 'Select end date',
      },
    ],
    // Filter logic function
    applyFilter: (
      items: ActivityLog[],
      filters: {
        userId?: string[];
        entityType?: string[];
        action?: string[];
        ipAddress?: string[] | string; // text filter: converted as string by filterUtils
        startDate?: string[];
        endDate?: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // User filter
        if (filters.userId && filters.userId.length > 0) {
          const userIdStr = item.userId?.toString() || '';
          if (!filters.userId.includes(userIdStr)) return false;
        }

        // Entity type filter
        if (filters.entityType && filters.entityType.length > 0) {
          if (!filters.entityType.includes(item.entityType)) return false;
        }

        // Action filter
        if (filters.action && filters.action.length > 0) {
          if (!filters.action.includes(item.action)) return false;
        }

        // IP address filter (partial match) — text filter comes as string, not array
        const ipFilterRaw = filters.ipAddress;
        const ipFilter =
          typeof ipFilterRaw === 'string'
            ? ipFilterRaw.trim().toLowerCase()
            : Array.isArray(ipFilterRaw) && ipFilterRaw.length > 0
              ? ipFilterRaw[0]?.trim().toLowerCase()
              : '';
        if (ipFilter && (!item.ipAddress || !item.ipAddress.toLowerCase().includes(ipFilter))) return false;

        // Date range filter
        if (ranges.startDate || ranges.endDate) {
          if (!item.createdAt) return false;
          const itemDate = new Date(item.createdAt);
          
          if (ranges.startDate?.start) {
            if (itemDate < ranges.startDate.start) return false;
          }
          if (ranges.endDate?.end) {
            const endDate = new Date(ranges.endDate.end);
            endDate.setHours(23, 59, 59, 999);
            if (itemDate > endDate) return false;
          }
        }

        return true;
      });
    },
    // Search box: filter by user name, entity type, action, IP, entity id, device/browser
    applySearch: (items: ActivityLog[], searchTerm: string): ActivityLog[] => {
      const q = searchTerm.toLowerCase().trim();
      if (!q) return items;
      return items.filter((item) => {
        const userDisplay =
          [item.user?.userDisplayName, item.user?.userFirstName, item.user?.userLastName, item.user?.userEmail]
            .filter(Boolean)
            .join(' ')
            .toLowerCase() || '';
        const entityType = (item.entityType || '').toLowerCase();
        const action = (item.action || '').toLowerCase();
        const ip = (item.ipAddress || '').toLowerCase();
        const entityId = String(item.entityId ?? '');
        const userAgent = (item.userAgent || '').toLowerCase();
        return (
          userDisplay.includes(q) ||
          entityType.includes(q) ||
          action.includes(q) ||
          ip.includes(q) ||
          entityId.includes(q) ||
          userAgent.includes(q)
        );
      });
    },
  },

  // Toolbar Actions
  toolbar: {
    actions: [],
    download: {
      enabled: true,
      formats: ['csv', 'excel', 'pdf'],
    },
  },

  // Table Configuration
  table: {
    actionsPosition: 'right' as const,
    onRowClick: (item: ActivityLog) => {
      window.location.href = `/activity-logs/${item.id}`;
    },
    keyExtractor: (item: ActivityLog) => item.id,
  },

  // Empty States
  emptyStates: {
    noData: 'No activity logs found.',
    noSearchResults: 'No activity logs found matching your search criteria.',
    noFilterResults: 'No activity logs match the selected filters.',
  },

  // Delete Configuration
  delete: {
    title: 'Delete Activity Log',
    message: 'Activity logs cannot be deleted as they are part of the audit trail.',
    confirmText: 'OK',
    cancelText: 'Cancel',
    errorMessage: 'Activity logs cannot be deleted.',
    handler: async () => {
      throw new Error('Activity logs cannot be deleted.');
    },
  },

  // Export Configuration
  export: {
    enabled: true,
    formats: ['csv', 'excel', 'pdf'],
  },

  // Utils
  utils: {
    formatDate: formatDateGlobal,
    formatDateTime,
  },

  // Fields Configuration (for forms - not used for activity logs but required)
  fields: [] as FieldConfig[],
};

const ACTION_LABELS: Record<string, string> = {
  CREATE: 'Create',
  UPDATE: 'Update',
  DELETE: 'Delete',
};

// Helper function to get action badge color
const getActionBadgeColor = (action: string): 'default' | 'secondary' | 'accent' | 'danger' | 'warning' | 'info' => {
  switch (action) {
    case 'CREATE':
      return 'accent';
    case 'UPDATE':
      return 'info';
    case 'DELETE':
      return 'danger';
    default:
      return 'default';
  }
};

// Helper function to get entity type badge color
const getEntityTypeBadgeColor = (_entityType: string): 'default' | 'secondary' | 'accent' | 'danger' | 'warning' | 'info' => {
  // Use a single neutral-ish color for all entity pills to improve visual consistency
  return 'info';
};

// Helper function to get entity route
const getEntityRoute = (entityType: string, entityId: number): string => {
  const routeMap: Record<string, (id: number) => string> = {
    Client: (id) => `/clients/${id}`,
    Task: (id) => `/tasks/${id}`,
    Ticket: (id) => `/support-tickets/${id}`,
    Lead: (id) => `/leads/${id}`,
    Note: (id) => `/notes/${id}`,
    Todo: (id) => `/todos/${id}`,
    Project: (id) => `/projects/${id}`,
    Package: (id) => `/packages/${id}`,
    SupportAgreement: (id) => `/support-agreements/${id}`,
    Invoice: (id) => `/invoices/${id}`,
    InvoicePayment: (id) => `/invoice-payments/${id}`,
    Appointment: (id) => `/appointments/${id}`,
    Call: (id) => `/calls/${id}`,
    Reminder: (id) => `/reminders/${id}`,
    ClientService: (id) => `/client-services/${id}`,
    PackageTask: (id) => `/packages/${id}`,
    SupportAgreementTask: (id) => `/support-agreement-tasks/${id}`,
    ClientContact: (id) => `/client-contacts/${id}`,
    TicketComment: (id) => `#`,
    Expense: (id) => `/expenses/${id}`,
    Conversation: (id) => `#`,
    FileAttachment: (id) => `#`,
    User: (id) => `/users/${id}`,
    UserInvitation: (id) => `#`,
    EmailAccount: (id) => `#`,
    KnowledgeBaseCategory: (id) => `#`,
    KnowledgeBaseArticle: (id) => `/knowledge-base/${id}`,
    CustomField: (id) => `#`,
    Workflow: (id) => `/workflows/${id}`,
    FilterPreset: (id) => `#`,
    TablePreset: (id) => `#`,
    ConditionalFormattingRule: (id) => `#`,
    ReportTemplate: (id) => `#`,
    DashboardLayout: (id) => `#`,
    Tag: (id) => `/tags/${id}`,
  };
  const routeFn = routeMap[entityType];
  return routeFn ? routeFn(entityId) : '#';
};

// Helper function to get columns
export const getColumns = (formatDate: (dateString?: string) => string): Column<ActivityLog>[] => [
  {
    key: 'user',
    header: 'User',
    sortable: true,
    render: (value, item) => {
      const userName = item.user?.userDisplayName || 
        `${item.user?.userFirstName || ''} ${item.user?.userLastName || ''}`.trim() ||
        item.user?.userEmail ||
        'Unknown';
      return (
        <div className="flex items-center gap-2">
          <span>{userName}</span>
        </div>
      );
    },
  },
  {
    key: 'action',
    header: 'Action',
    sortable: true,
    render: (_value, item) => {
      if (!item) return null;
      const action = item.action ?? '';
      return (
        <Badge variant={getActionBadgeColor(action)}>
          {ACTION_LABELS[action] ?? action}
        </Badge>
      );
    },
  },
  {
    key: 'entityType',
    header: 'Entity Type',
    sortable: true,
    render: (_value, item) => {
      if (!item) return null;
      const label = ENTITY_TYPE_LABELS[item.entityType] ?? item.entityType ?? '—';
      return (
        <Badge variant={getEntityTypeBadgeColor(item.entityType)}>
          {label}
        </Badge>
      );
    },
  },
  {
    key: 'entity',
    header: 'Entity',
    sortable: false,
    render: (_value, item) => {
      if (!item) return null;
      const route = getEntityRoute(item.entityType, item.entityId);
      return (
        <Link
          to={route}
          className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
        >
          {ENTITY_TYPE_LABELS[item.entityType] ?? item.entityType} #{item.entityId}
        </Link>
      );
    },
  },
  {
    key: 'hasChanges',
    header: 'Changes',
    sortable: false,
    render: (_value, item) => {
      if (!item) return null;
      const raw = item.changes as unknown;
      const hasChanges =
        raw != null &&
        (typeof raw === 'string'
          ? (raw as string).trim() !== ''
          : ((raw as { before?: unknown; after?: unknown }).before != null ||
              (raw as { before?: unknown; after?: unknown }).after != null));
      return (
        <Badge variant={hasChanges ? 'info' : 'secondary'}>
          {hasChanges ? 'Yes' : 'No'}
        </Badge>
      );
    },
  },
  {
    key: 'ipAddress',
    header: 'IP Address',
    sortable: true,
    render: (_value, item) => (item?.ipAddress ? item.ipAddress : '—'),
  },
  {
    key: 'userAgent',
    header: 'Device / Browser',
    sortable: false,
    render: (_value, item) => (item ? parseUserAgentShort(item.userAgent) : null),
  },
  {
    key: 'createdAt',
    header: 'Timestamp',
    sortable: true,
    render: (_value, item) => (
      <span title={item?.createdAt ? formatDateTime(item.createdAt) : undefined}>
        {item?.createdAt ? formatRelativeTime(item.createdAt) : '—'}
      </span>
    ),
  },
];

// Helper function to get toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'settings':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
  }
};

// Helper function to get table actions (View only - no delete in UI)
export const getTableActions = (
  item: ActivityLog,
  _onDelete: (id: number) => void,
  _a?: unknown,
  _b?: unknown,
  _options?: { canDelete?: boolean }
): React.ReactNode => {
  return (
    <StandardTableActions
      viewTo={`/activity-logs/${item.id}`}
      canEdit={false}
      canDelete={false}
    />
  );
};

// Detail page configuration (read-only: no edit)
export const detail = {
  subtitle: 'Activity Log Details',
  notFoundTitle: 'Activity Log Not Found',
  notFoundMessage: 'The activity log you are looking for does not exist.',
  notFoundBackText: 'Back to Activity Logs',
  defaultTitle: 'Activity Log',
  getTitle: (item: ActivityLog) => `Activity Log #${item.id}`,
  canEdit: false,
};

// Helper function to get detail sections
export const getDetailSections = (
  item: ActivityLog,
  formatDate: (dateString?: string) => string
) => {
  const userName = item.user?.userDisplayName || 
    `${item.user?.userFirstName || ''} ${item.user?.userLastName || ''}`.trim() ||
    item.user?.userEmail ||
    'Unknown';

  return [
    {
      title: 'Activity Information',
      icon: '📋',
      fields: [
        {
          label: 'User',
          value: userName,
        },
        {
          label: 'Action',
          value: (
            <Badge variant={getActionBadgeColor(item.action)}>
              {ACTION_LABELS[item.action] ?? item.action}
            </Badge>
          ),
        },
        {
          label: 'Entity Type',
          value: (
            <Badge variant={getEntityTypeBadgeColor(item.entityType)}>
              {ENTITY_TYPE_LABELS[item.entityType] ?? item.entityType ?? '—'}
            </Badge>
          ),
        },
        {
          label: 'Entity',
          value: (
            <Link
              to={getEntityRoute(item.entityType, item.entityId)}
              className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
            >
              {ENTITY_TYPE_LABELS[item.entityType] ?? item.entityType} #{item.entityId}
            </Link>
          ),
        },
        {
          label: 'Timestamp',
          value: formatDateTime(item.createdAt),
        },
      ],
    },
    {
      title: 'Changes',
      icon: '🔄',
      fields: [
        {
          label: 'Change Details',
          value: (() => {
            const raw = item.changes;
            if (raw == null) return <span className="text-gray-500">No changes recorded</span>;
            let changes: { before?: unknown; after?: unknown };
            if (typeof raw === 'string') {
              try {
                changes = JSON.parse(raw) as { before?: unknown; after?: unknown };
              } catch {
                changes = {};
              }
            } else {
              changes = raw as { before?: unknown; after?: unknown };
            }
            const before = changes.before;
            const after = changes.after;
            const hasBefore = before !== undefined && before !== null;
            const hasAfter = after !== undefined && after !== null;
            return (
              <div className="space-y-2">
                {item.action === 'CREATE' && hasAfter && (
                  <div className="bg-green-50 border border-green-200 rounded p-3 dark:bg-green-900/20 dark:border-green-800">
                    <div className="text-sm font-semibold text-green-800 dark:text-green-200 mb-2">Created:</div>
                    <pre className="text-xs text-green-700 dark:text-green-300 whitespace-pre-wrap break-all">
                      {JSON.stringify(after, null, 2)}
                    </pre>
                  </div>
                )}
                {item.action === 'DELETE' && hasBefore && (
                  <div className="bg-red-50 border border-red-200 rounded p-3 dark:bg-red-900/20 dark:border-red-800">
                    <div className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">Deleted:</div>
                    <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap break-all">
                      {JSON.stringify(before, null, 2)}
                    </pre>
                  </div>
                )}
                {item.action === 'UPDATE' && hasBefore && hasAfter && (
                  <>
                    {typeof before === 'object' && before !== null && typeof after === 'object' && after !== null && (
                      (() => {
                        const diffLines = getFieldLevelDiffSummary(before as Record<string, unknown>, after as Record<string, unknown>);
                        if (diffLines.length > 0) {
                          return (
                            <div className="rounded p-3 border bg-gray-50 border-gray-200 dark:bg-gray-800/50 dark:border-gray-700 mb-3">
                              <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Summary of changes:</div>
                              <ul className="text-xs text-gray-700 dark:text-gray-300 space-y-1 list-none">
                                {diffLines.map(({ key, beforeVal, afterVal }) => (
                                  <li key={key} className="font-mono">
                                    <span className="font-medium">{key}</span>: {formatDiffValue(beforeVal)} → {formatDiffValue(afterVal)}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        }
                        return null;
                      })()
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-red-50 border border-red-200 rounded p-3 dark:bg-red-900/20 dark:border-red-800">
                        <div className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">Before:</div>
                        <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap break-all">
                          {JSON.stringify(before, null, 2)}
                        </pre>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded p-3 dark:bg-green-900/20 dark:border-green-800">
                        <div className="text-sm font-semibold text-green-800 dark:text-green-200 mb-2">After:</div>
                        <pre className="text-xs text-green-700 dark:text-green-300 whitespace-pre-wrap break-all">
                          {JSON.stringify(after, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </>
                )}
                {((item.action === 'CREATE' && !hasAfter) ||
                  (item.action === 'DELETE' && !hasBefore) ||
                  (item.action === 'UPDATE' && (!hasBefore || !hasAfter))) && (
                  <div className="rounded p-3 border bg-gray-50 border-gray-200 dark:bg-gray-800/50 dark:border-gray-700">
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Raw changes:</div>
                    <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all">
                      {typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })(),
        },
      ],
    },
    {
      title: 'Technical Details',
      icon: '🔧',
      fields: [
        {
          label: 'IP Address',
          value: item.ipAddress || 'N/A',
        },
        {
          label: 'User Agent',
          value: item.userAgent || 'N/A',
        },
      ],
    },
  ];
};
